import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import Icon from '@mdi/react';
import { mdiClose, mdiSend } from '@mdi/js';
import { mdiInformationOutline } from '@mdi/js';
import { observer } from 'mobx-react';
import { Tooltip } from '../base';
import { useAi } from '../../services/ai-adapter/hook';
import { AiRequest } from '../../services/ai-adapter/protocol';
import { AiEntityDescriptor, aiEntitySchemas } from '../../services/ai-adapter/entities-description';
import { buildUtilityFromKey } from '../../services/ai-adapter/utility/utility';
import { normalizeMarkdown } from '../messaging/util';
import { BlockedComponent } from '../messaging/chat-panel/blocked-component';
import rawSuggestions from './suggestions.json';
import * as S from './ai-assistant.styles';
import talkToClaimGif from './TalkToClaim.gif';

type SuggestionId = string;

const generateLocalId = (): string => {
    const maybeCrypto = typeof crypto !== 'undefined' ? crypto : undefined;
    if (maybeCrypto && typeof (maybeCrypto as any).randomUUID === 'function') {
        return (maybeCrypto as any).randomUUID();
    }
    const base = Math.random().toString(36).slice(2) + Date.now().toString(36);
    return base.slice(0, 20);
};

interface ISuggestionDefinition {
    id: SuggestionId;
    label: string;
    template: string;
    action: AiRequest['action'];
}

type ISuggestionsMap = Record<string, ISuggestionDefinition[]>;

const suggestionsMap: ISuggestionsMap = rawSuggestions as ISuggestionsMap;

export interface IAiAssistantProps {
    /** Controls whether the floating assistant panel is visible. */
    open: boolean;
    /** Called when the panel should be closed (e.g. via Close/Cancel). */
    onClose: () => void;
    /** Either a simple model name (e.g. "SupportClaim") or a full descriptor. */
    entity: string | AiEntityDescriptor;
    subjectLabel?: string;
    /** Key into suggestions.json. Defaults to "default". */
    suggestionsKey?: string;
    /** Arbitrary data snapshot that will be serialized into context.dataJson. */
    contextData?: unknown;
    /** Key into utility-contexts.json describing app/page context. */
    utilityKey?: string;
}

interface IFloatingPosition {
    x: number;
    y: number;
}

export const AiAssistant: React.FC<IAiAssistantProps> = props => {
    const { open, onClose, entity, subjectLabel, suggestionsKey, contextData, utilityKey } = props;

    if (!open) {
        return null;
    }

    const effectiveSubject = subjectLabel || 'item';
    const effectiveSuggestionsKey = suggestionsKey || 'default';

    return (
        <FloatingAiPanel
            onClose={onClose}
            entity={entity}
            contextData={contextData}
            subjectLabel={effectiveSubject}
            suggestionsKey={effectiveSuggestionsKey}
            utilityKey={utilityKey}
        />
    );
};

interface IFloatingAiPanelProps {
    entity: string | AiEntityDescriptor;
    contextData?: unknown;
    onClose: () => void;
    subjectLabel?: string;
    suggestionsKey: string;
    utilityKey?: string;
}

const FloatingAiPanel: React.FC<IFloatingAiPanelProps> = observer(({
    entity,
    contextData,
    onClose,
    subjectLabel,
    suggestionsKey,
    utilityKey,
}) => {
    const context: AiRequest['context'] = useMemo(() => {
        const entityDescriptor: AiEntityDescriptor =
            typeof entity === 'string' ? { name: entity, ...(aiEntitySchemas[entity] || {}) } : entity;

        let dataJson: string | undefined;
        if (contextData !== undefined) {
            try {
                dataJson = JSON.stringify(contextData);
            } catch {
                dataJson = undefined;
            }
        }

        return {
            entity: entityDescriptor,
            dataJson,
        };
    }, [entity, contextData]);

    const { askAi, loading, error } = useAi();
    const [messages, setMessages] = useState<{ id: string; role: 'user' | 'assistant'; text: string }[]>([]);
    const [question, setQuestion] = useState('');
    const [internalError, setInternalError] = useState<string | null>(null);
    const [showHowToGif, setShowHowToGif] = useState(false);
    const [position, setPosition] = useState<IFloatingPosition>(() => {
        const defaultX = 0;
        const defaultY = 140;
        if (typeof window === 'undefined') {
            return { x: defaultX, y: defaultY };
        }
        const x = Math.max(0, window.innerWidth - 650);
        const y = Math.max(0, defaultY);
        return { x, y };
    });
    const [dragging, setDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
    const [action, setAction] = useState<AiRequest['action']>('explain');
    const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
    const [suggestedReplySegments, setSuggestedReplySegments] = useState<
        Array<{ type: 'text' | 'reply'; text: string }>
    >([]);
    const messagesContainerRef = React.useRef<HTMLDivElement | null>(null);
    const messagesEndRef = React.useRef<HTMLDivElement | null>(null);

    const presets = useMemo(() => suggestionsMap[suggestionsKey] || suggestionsMap.default || [], [suggestionsKey]);

    const parseSuggestReplyResponseToSegments = (raw: string): Array<{ type: 'text' | 'reply'; text: string }> => {
        const text = raw ?? '';
        if (!text.trim()) return [];

        const markerRe = /<<<REPLY_START>>>\s*([\s\S]*?)\s*<<<REPLY_END>>>/gi;
        const segments: Array<{ type: 'text' | 'reply'; text: string }> = [];

        let lastIndex = 0;
        let match: RegExpExecArray | null = null;
        while ((match = markerRe.exec(text)) !== null) {
            const start = match.index ?? 0;
            const outside = text.slice(lastIndex, start).trim();
            if (outside) {
                segments.push({ type: 'text', text: outside });
            }

            const reply = (match[1] ?? '').trim();
            if (reply) {
                segments.push({ type: 'reply', text: reply });
            }

            lastIndex = start + match[0].length;
        }

        const tail = text.slice(lastIndex).trim();
        if (tail) {
            segments.push({ type: 'text', text: tail });
        }

        return segments;
    };

    const handleMouseDownHeader = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragging(true);
        setDragOffset({
            x: event.clientX - position.x,
            y: event.clientY - position.y,
        });
    };

    useEffect(() => {
        if (!dragging || !dragOffset) {
            return;
        }

        const handleMouseMove = (event: MouseEvent) => {
            event.preventDefault();
            const nextX = event.clientX - dragOffset.x;
            const nextY = event.clientY - dragOffset.y;

            setPosition({
                x: nextX,
                y: nextY < 0 ? 0 : nextY,
            });
        };

        const handleMouseUp = () => {
            setDragging(false);
            setDragOffset(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragging, dragOffset]);

    const handleSend = async () => {
        const trimmed = question.trim();
        if (!trimmed || loading) {
            return;
        }

        setInternalError(null);
        setSuggestedReplySegments([]);
        const isSuggestReply = selectedPresetId === 'suggestReply';

        const userMessage = trimmed;
        setMessages(prev => [...prev, { id: generateLocalId(), role: 'user', text: userMessage }]);
        setQuestion('');

        try {
            let utility = buildUtilityFromKey(utilityKey);

            if (isSuggestReply) {
                utility += `\n\nFormatting requirements: For EACH suggested reply, wrap it inside markers:\n<<<REPLY_START>>>\nyour reply here\n<<<REPLY_END>>>\n\nAny text outside markers will be treated as regular text and will be displayed by the frontend in the same order (text, reply box, text, reply box). Copy action must include only marker reply contents.`;
            }

            const result = await askAi(action, trimmed, context, utility);
            if (isSuggestReply) {
                const segments = parseSuggestReplyResponseToSegments(result);
                setSuggestedReplySegments(segments);
            } else {
                setSuggestedReplySegments([]);
                setMessages(prev => [...prev, { id: generateLocalId(), role: 'assistant', text: result }]);
            }
        } catch (e: any) {
            setInternalError(typeof e === 'string' ? e : e?.message || 'AI error');
            setSuggestedReplySegments([]);
        }
    };

    const handleCancel = () => {
        setQuestion('');
        setInternalError(null);
        setShowHowToGif(false);
        onClose();
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = event => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            void handleSend();
        }
    };

    useEffect(() => {
        // For Q&A-style responses we want the user to see from the beginning.
        messagesContainerRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    }, [messages.length, suggestedReplySegments]);

    const hasAssistantReply =
        messages.some(m => m.role === 'assistant') || suggestedReplySegments.some(seg => seg.type === 'reply');
    const lastAssistantText = useMemo(
        () =>
            [...messages]
                .reverse()
                .find(m => m.role === 'assistant')
                ?.text ?? '',
        [messages],
    );

    const content = (
        <S.FloatingContainer
            isDragging={dragging}
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
            }}>
            <S.Header onMouseDown={handleMouseDownHeader}>
            <S.HeaderGroup>
                <S.Title>Talk to AI</S.Title>
                <Tooltip
                        direction="top"
                        variant="dark"
                        title={<span>How to use this?</span>}>
                        <div
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => {
                                e.stopPropagation();
                            }}>
                            <S.IconButton
                                variant="text"
                                type="button"
                                onClick={() => setShowHowToGif(v => !v)}>
                                <Icon path={mdiInformationOutline} size={0.75} />
                            </S.IconButton>
                        </div>
                    </Tooltip>
            </S.HeaderGroup>
                <S.HeaderActions>              
                    <S.CloseButton variant="text" onClick={handleCancel}>
                        <Icon path={mdiClose} size={0.8} />
                    </S.CloseButton>
                </S.HeaderActions>
            </S.Header>
            {showHowToGif && (
                <S.HelpGifContainer onMouseDown={e => e.stopPropagation()}>
                    <S.HelpGifTopRow>
                        <S.Title>How to use this?</S.Title>
                        <S.HelpGifCloseButton
                            variant="text"
                            type="button"
                            onClick={e => {
                                e.stopPropagation();
                                setShowHowToGif(false);
                            }}>
                            <Icon path={mdiClose} size={0.8} />
                        </S.HelpGifCloseButton>
                    </S.HelpGifTopRow>
                    <S.HelpGifImage src={talkToClaimGif} alt="Talk to AI instructions" />
                </S.HelpGifContainer>
            )}
            {messages.length > 0 && (
                <S.Body>
                    <S.MessagesContainer ref={messagesContainerRef}>
                        {messages.map(message => (
                            <S.MessageRow key={message.id} role={message.role}>
                                <S.MessageBubble role={message.role}>
                                    {message.role === 'assistant' ? (
                                        <S.MarkdownBlock
                                            options={{
                                                overrides: {
                                                    iframe: {
                                                        component: BlockedComponent,
                                                    },
                                                },
                                            }}>
                                            {normalizeMarkdown(message.text)}
                                        </S.MarkdownBlock>
                                    ) : (
                                        message.text
                                    )}
                                </S.MessageBubble>
                            </S.MessageRow>
                        ))}
                        {loading && (
                            <S.MessageRow role="assistant">
                                <S.MessageBubble role="assistant">
                                    Thinking…

                                    <div>This may take up to 5 minutes. Please don’t close this window.</div>
                                </S.MessageBubble>
                            </S.MessageRow>
                        )}
                        {(error || internalError) && <S.ErrorText>{internalError || error}</S.ErrorText>}
                        {suggestedReplySegments.length > 0 &&
                            suggestedReplySegments.map((seg, idx) =>
                                seg.type === 'text' ? (
                                    <S.MessageRow key={`seg-${idx}`} role="assistant">
                                        <S.MessageBubble role="assistant">
                                            <S.MarkdownBlock
                                                options={{
                                                    overrides: {
                                                        iframe: {
                                                            component: BlockedComponent,
                                                        },
                                                    },
                                                }}>
                                                {normalizeMarkdown(seg.text)}
                                            </S.MarkdownBlock>
                                        </S.MessageBubble>
                                    </S.MessageRow>
                                ) : (
                                    <S.SuggestedReplyContainer key={`seg-${idx}`}>
                                        <S.MarkdownBlock
                                            options={{
                                                overrides: {
                                                    iframe: {
                                                        component: BlockedComponent,
                                                    },
                                                },
                                            }}>
                                            {normalizeMarkdown(seg.text)}
                                        </S.MarkdownBlock>
                                        <div style={{ marginTop: 8, textAlign: 'right' }}>
                                            <S.SecondaryButton
                                                variant="outlined"
                                                type="button"
                                                onClick={() => {
                                                    if (navigator?.clipboard?.writeText) {
                                                        void navigator.clipboard.writeText(seg.text);
                                                    }
                                                }}>
                                                Copy answer
                                            </S.SecondaryButton>
                                        </div>
                                    </S.SuggestedReplyContainer>
                                ),
                            )}

                        {!suggestedReplySegments.some(seg => seg.type === 'reply') && hasAssistantReply && lastAssistantText && (
                            <div style={{ marginTop: 8, textAlign: 'right' }}>
                                <S.SecondaryButton
                                    variant="outlined"
                                    type="button"
                                    onClick={() => {
                                        if (navigator?.clipboard?.writeText) {
                                            void navigator.clipboard.writeText(lastAssistantText);
                                        }
                                    }}
                                >
                                    Copy answer
                                </S.SecondaryButton>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </S.MessagesContainer>
                </S.Body>
            )}
            {!hasAssistantReply && (
                <S.Footer>
                    {presets.length > 0 && (
                        <S.PresetButtonsRow>
                            {presets.map(preset => (
                                <S.SecondaryButton
                                    key={preset.id}
                                    variant="outlined"
                                    type="button"
                                    onClick={() => {
                                        setAction(preset.action);
                                        setSelectedPresetId(preset.id);
                                        setQuestion(
                                            preset.template.replace(
                                                /\{\{\s*subject\s*\}\}/gi,
                                                subjectLabel || 'item',
                                            ),
                                        );
                                    }}>
                                    {preset.label}
                                </S.SecondaryButton>
                            ))}
                        </S.PresetButtonsRow>
                    )}
                    <S.InputWrapper>
                        <S.InputContainer>
                            <S.InputBox>
                                <S.Textarea
                                    value={question}
                                    onChange={event => setQuestion(event.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Type your question for AI..."
                                    disabled={loading}
                                    disableLatin1Validation
                                    rows={2}
                                />
                            </S.InputBox>
                            <S.ActionsWrapper>
                                <S.IconButton
                                    variant="primary"
                                    type="button"
                                    onClick={handleSend}
                                    disabled={loading || !question.trim()}>
                                    <Icon path={mdiSend} size={0.7} />
                                </S.IconButton>
                            </S.ActionsWrapper>
                        </S.InputContainer>
                    </S.InputWrapper>
                </S.Footer>
            )}
        </S.FloatingContainer>
    );

    return ReactDOM.createPortal(content, document.body);
});
